import {Component, Input, OnInit, ViewEncapsulation} from '@angular/core';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {v4 as uuidv4} from 'uuid';
import {
    Availabilities,
    BaseStepperFormComponent,
    CompDefinitionAvailabilities,
    FileUploadService,
    FileUploadType,
    filterList,
    LoadingService,
    Menu,
    MenuItem,
    MenuItemLabel,
    MenuItemModifier,
    MenuItemModifierGroup,
    MenuItemModifierSelection,
    MenuItemModifierSelectionOption,
    MenuItemPrice,
    MenuItemSelection,
    MenuItemService,
    MenuItemTopping,
    MenuItemToppingRule,
    MenuLabel,
    MenuModifier,
    MenuModifierGroup,
    MenuModifierSelection,
    MenuSelection,
    MenuService,
    MenuTaxLabel,
    MenuTopping,
    Organization,
    OrganizationRewardProgram,
    OrganizationService,
    reorder,
    RewardProgramTypes,
    RewardTypes,
    sortAlphabetical,
    sortBySortOrder,
    ToastService,
    UploadImageModalComponent,
    WifiPrinter,
    WifiPrinterService
} from 'brewbill-lib';
import {ModalController, Platform} from '@ionic/angular';
import {EditMenuItemPriceComponent} from '../edit-menu-item-price/edit-menu-item-price.component';

@Component({
    selector: 'app-edit-menu-item',
    templateUrl: './edit-menu-item.component.html',
    styleUrls: ['./edit-menu-item.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class EditMenuItemComponent extends BaseStepperFormComponent implements OnInit {
    @Input() menuItem: any;
    @Input() addMenuModifier: any;
    @Input() addMenuModifierSelection: any;
    @Input() addMenuModifierGroup: any;
    @Input() addMenuLabel: any;
    @Input() addMenuTaxLabel: any;
    @Input() addMenuTopping: any;
    @Input() addMenuSelection: any;
    @Input() hideCategories: boolean;
    @Input() rewardProgram: OrganizationRewardProgram;

    sortAlphabetical = sortAlphabetical;

    availability: string;
    menu: Menu;
    organization: Organization;
    prices: MenuItemPrice[] = [];
    selectedModifiers = [];
    selectedLabels: MenuItemLabel[] = [];
    selectedTaxLabels: MenuTaxLabel[] = [];
    selectedToppings: MenuItemTopping[] = [];
    selectedSelections: MenuItemSelection[];
    filteredSelections: MenuSelection[];
    wifiPrinters: WifiPrinter[];
    informationFormGroup: FormGroup = new FormGroup({});
    availableOrderComps = [];
    availableBoGoOrderComps = [];

    availabilities = Availabilities;
    rewardProgramTypes = RewardProgramTypes;
    rewardTypes = RewardTypes;

    constructor(
        modalController: ModalController,
        private formBuilder: FormBuilder,
        private toastService: ToastService,
        private loadingService: LoadingService,
        private menuItemService: MenuItemService,
        private menuService: MenuService,
        private organizationService: OrganizationService,
        private wifiPrinterService: WifiPrinterService,
        private platform: Platform,
        private fileUploadService: FileUploadService
    ) {
        super(modalController);
    }

    ngOnInit() {
        this.organization = this.organizationService.currentValue;
        this.menu = this.menuService.currentValue;
        this.availability = this.menuItem != null ? this.menuItem.availability : Availabilities.ALL;
        this.menuItem = this.menuItem == null ? new MenuItem() : this.menuItem;
        this.selectedLabels = this.menuItem.labels == null ? [] : [...this.menuItem.labels];
        this.selectedTaxLabels = this.menuItem.taxLabels == null ? [] : [...this.menuItem.taxLabels];
        this.selectedToppings = this.menuItem.toppings == null ? [] : [...this.menuItem.toppings];
        this.selectedSelections = this.menuItem.selections == null ? [] : [...this.menuItem.selections];
        this.setPriceKeys(this.menuItem.prices == null ? [] : this.menuItem.prices);
        this.wifiPrinters = this.wifiPrinterService.printers;

        let tempModifiers = [];
        if (!!this.menuItem.modifiers) {
            tempModifiers = [...this.menuItem.modifiers];
        }
        if (!!this.menuItem.modifierSelections) {
            tempModifiers = tempModifiers.concat(this.menuItem.modifierSelections);
        }
        if (!!this.menuItem.modifierGroups) {
            tempModifiers = tempModifiers.concat(this.menuItem.modifierGroups);
        }

        this.selectedModifiers = sortBySortOrder(tempModifiers);

        const currentPickupLocation = !!this.organization && !!this.organization.pickupLocations && !!this.menuItem.pickupLocationId
            ? this.organization.pickupLocations.find(p => p.id === this.menuItem.pickupLocationId) : null;

        const currentPrinter = !!this.wifiPrinters && !!this.menuItem && !!this.menuItem.wifiPrinter ?
            this.wifiPrinters.find(p => p.id === this.menuItem.wifiPrinter.id) : null;

        this.availableOrderComps = !!this.organization && !!this.organization.compDefinitions
            ? this.organization.compDefinitions.filter(c => c.availability === CompDefinitionAvailabilities.ORDER_ITEM) : [];

        this.availableBoGoOrderComps = !!this.organization && !!this.organization.compDefinitions
            ? this.organization.compDefinitions
                .filter(c => c.availability === CompDefinitionAvailabilities.ORDER_ITEM && c.requiredCount > 0)
            : [];

        this.filteredSelections = !!this.menuItem.id
            ? this.menu.menuSelections.filter(s =>
                !s.options.some(o => this.menuItem.id === o.menuItem.id)) : this.menu.menuSelections;

        if (this.prices.length === 0) {
            const price = new MenuItemPrice();
            price.name = 'Default';
            price.price = 0;
            price.sortOrder = 0;
            price.availability = Availabilities.ALL;
            price.defaultPrice = true;
            price.key = uuidv4();
            this.prices = [price];
        }

        this.informationFormGroup = this.formBuilder.group(
            {
                id: this.menuItem.id,
                parentId: this.menu.id,
                available: this.menuItem.availability == null ? Availabilities.ALL : this.menuItem.availability,
                name: [this.menuItem.name, Validators.required],
                sku: this.menuItem.sku,
                flavorText: this.menuItem.flavorText,
                description: this.menuItem.description,
                priceLabel: this.menuItem.priceLabel,
                verifyAge: this.menuItem.verifyAge,
                terminalOnly: this.menuItem.terminalOnly,
                imageAddress: this.menuItem.imageAddress,
                featuredColor: !!this.menuItem.featuredColor ? this.menuItem.featuredColor : 'primary',
                taxOverride: !!this.menuItem.taxOverride ? this.menuItem.taxOverride : '',
                menuCategoryIds: [this.menuItem.menuCategoryIds],
                wifiPrinter: currentPrinter,
                pickupLocationId: !!currentPickupLocation ? currentPickupLocation.id : '',
                requestTableStandMarker: this.menuItem.requestTableStandMarker,
                requireOrderFlow: this.menuItem.requireOrderFlow,
                requireModal: this.menuItem.requireModal,
                requireFirstName: this.menuItem.requireFirstName,
                requireLastName: this.menuItem.requireLastName,
                requireEmail: this.menuItem.requireEmail,
                requireMobile: this.menuItem.requireMobile,
                sendTextOnReady: this.menuItem.sendTextOnReady,
                requirePersonPerItem: this.menuItem.requirePersonPerItem,
                freeOptionalToppingCount: [
                    !this.menuItem.freeOptionalToppingCount ? 0 : this.menuItem.freeOptionalToppingCount, Validators.min(0)
                ],
                maxOptionalToppingCount: [
                    !this.menuItem.maxOptionalToppingCount ? null : this.menuItem.maxOptionalToppingCount
                ],
                menuItemToppingRule: !!this.menuItem.menuItemToppingRule
                    ? this.menuItem.menuItemToppingRule : MenuItemToppingRule.LEAST_EXPENSIVE
            }
        );
    }

    setPriceKeys(menuItemPrices) {
        const temp = !!menuItemPrices ? [...menuItemPrices] : [];
        temp.forEach(p => {
            if (!p.key) {
                p.key = uuidv4();
            }
        });
        this.prices = temp;
    }

    valid() {
        return this.informationFormGroup.valid && this.validPrices();
    }

    validPrices() {
        return this.prices.length > 0
            && this.prices.filter(s => {
                return s.price != null;
            }).length === this.prices.length;
    }

    selectTaxLabel(label: MenuTaxLabel) {
        if (this.selectedTaxLabels.find(obj => obj.id === label.id) == null) {
            this.selectedTaxLabels = [...this.selectedTaxLabels, label];
        }
    }

    removeTopping(item: MenuItemTopping) {
        this.selectedToppings = this.selectedToppings.filter(t => t.topping.id !== item.topping.id);
    }

    markToppings(items: any[], selectedItems: any[], selectionParam: string, disabledParam: string) {
        items.forEach(i => {
            i[selectionParam] = selectedItems.some(s => s.topping.id === i.id);
        });
    }

    updateSelectedToppings(toppings: any[]) {
        const update = this.selectedToppings.filter(t =>
            toppings.some(i => i.id === t.topping.id));

        toppings.filter(i => !update.some(t => t.topping.id === i.id)).forEach(top => {
            update.push(new MenuItemTopping({
                topping: top as MenuTopping,
                prices: [],
                included: true,
                sortOrder: update.length
            }));
        });

        this.selectedToppings = update.sort((a: MenuItemTopping, b: MenuItemTopping) => {
            if (a.included && !b.included) {
                return -1;
            } else if (!a.included && b.included) {
                return 1;
            } else {
                return a.sortOrder - b.sortOrder;
            }
        });
    }

    sortToppingsAlphabetical() {
        this.selectedToppings = this.selectedToppings.sort((a: MenuItemTopping, b: MenuItemTopping) => {
            if (a.included && !b.included) {
                return -1;
            } else if (!a.included && b.included) {
                return 1;
            } else {
                return a.topping.name.localeCompare(b.topping.name);
            }
        });
    }

    removeSelection(item: MenuItemSelection) {
        this.selectedSelections = this.selectedSelections.filter(t => t.menuSelection.id !== item.menuSelection.id);
    }

    markSelections(items: any[], selectedItems: any[], selectionParam: string, disabledParam: string) {
        items.forEach(i => {
            i[selectionParam] = selectedItems.some(s => s.menuSelection.id === i.id);
        });
    }

    updateSelectedSelections(selections: any[]) {
        const update = this.selectedSelections.filter(t =>
            selections.some(i => i.id === t.menuSelection.id));

        selections.filter(i => !update.some(t => t.menuSelection.id === i.id)).forEach(s => {
            update.push(new MenuItemSelection({
                menuSelection: s as MenuSelection,
                sortOrder: update.length
            }));
        });

        this.selectedSelections = sortBySortOrder(update);
    }

    removeLabel(item: MenuItemLabel) {
        this.selectedLabels = this.selectedLabels.filter(t => t.label.id !== item.label.id);
    }

    markLabels(items: any[], selectedItems: any[], selectionParam: string, disabledParam: string) {
        items.forEach(i => {
            i[selectionParam] = selectedItems.some(s => s.label.id === i.id);
        });
    }

    updateSelectedLabels(labels: any[]) {
        const update = this.selectedLabels.filter(t =>
            labels.some(i => i.id === t.label.id));

        labels.filter(i => !update.some(t => t.label.id === i.id)).forEach(s => {
            update.push(new MenuItemLabel({
                label: s as MenuLabel,
                sortOrder: update.length
            }));
        });

        this.selectedLabels = sortBySortOrder(update);
    }

    removeTaxLabel(item: MenuTaxLabel) {
        this.selectedTaxLabels = this.selectedTaxLabels.filter(t => t.id !== item.id);
    }

    markTaxLabels(items: any[], selectedItems: any[], selectionParam: string, disabledParam: string) {
        items.forEach(i => {
            i[selectionParam] = selectedItems.some(s => s.id === i.id);
        });
    }

    updateSelectedTaxLabels(labels: any[]) {
        const update = this.selectedTaxLabels.filter(t =>
            labels.some(i => i.id === t.id));

        labels.filter(i => !update.some(t => t.id === i.id)).forEach(s => {
            update.push(s as MenuTaxLabel);
        });

        this.selectedTaxLabels = update;
    }

    filterByName(items: any[], filter: string) {
        return filterList(items, filter, 'name');
    }

    updateSelectionSortOrder(list: MenuItemSelection[]) {
        this.selectedSelections = list;
    }

    reorderPrices(event) {
        reorder(this.prices, event);
    }

    async openEditPrice(menuItemPrice: MenuItemPrice) {
        const index = menuItemPrice != null ? this.prices.indexOf(menuItemPrice) : -1;

        const modal = await this.modalController.create({
            component: EditMenuItemPriceComponent,
            componentProps: {
                menuItemPrice,
                defaultAvailability: this.availability
            },
            cssClass: 'menu-modal'
        });

        await modal.present();
        await modal.onDidDismiss().then((dataReturned) => {
            if (dataReturned !== null && dataReturned.data != null) {
                const price = dataReturned.data;
                if (price.defaultPrice) {
                    for (const p of this.prices) {
                        p.defaultPrice = false;
                    }
                }

                const temp = [...this.prices];
                if (index === -1) {
                    price.sortOrder = this.prices.length;
                    temp.push(price);
                } else {
                    const existing = this.prices[index];
                    price.menuSelectionIds = existing.menuSelectionIds;
                    price.compDefinitionIds = existing.compDefinitionIds;
                    price.compDefinitionConditionIds = existing.compDefinitionConditionIds;
                    price.tokenGroupIds = existing.tokenGroupIds;
                    temp.splice(index, 1, price);
                }
                this.setPriceKeys(temp);
            }
        });
    }

    removePrice(event, menuItemPrice: MenuItemPrice) {
        event.stopPropagation();
        const temp = [...this.prices];
        temp.splice(temp.indexOf(menuItemPrice), 1);
        this.setPriceKeys(temp);
    }

    async openAddMenuLabel() {
        const l = await this.addMenuLabel(null);
        if (l != null) {
            const menuItemLabel = new MenuItemLabel();
            menuItemLabel.label = l;
            menuItemLabel.sortOrder = this.selectedLabels.length;
            this.selectedLabels = [...this.selectedLabels, menuItemLabel];
        }
    }

    async openAddMenuTaxLabel() {
        const l = await this.addMenuTaxLabel(null);
        if (l != null) {
            this.selectTaxLabel(l);
        }
    }

    buildModifierItem(item: any, sortOrder: number) {
        if (!!item.options) {
            const options: MenuItemModifierSelectionOption[] = [];
            item.options.forEach(o => options.push(new MenuItemModifierSelectionOption({
                menuModifier: o.menuModifier,
                additionalPrice: null
            })));

            return new MenuItemModifierSelection({
                menuModifierSelection: item as MenuModifierSelection,
                sortOrder,
                options
            });
        } else if (!!item.selections) {
            return new MenuItemModifierGroup({
                menuModifierGroup: item as MenuModifierGroup,
                sortOrder
            });
        } else {
            return new MenuItemModifier({
                menuModifier: item as MenuModifier,
                additionalPrice: null,
                sortOrder
            });
        }
    }

    async openAddMenuModifier() {
        const t = await this.addMenuModifier(null);
        if (t != null) {
            const mit = new MenuItemModifier();
            mit.menuModifier = t;
            mit.sortOrder = this.selectedModifiers.length;
            this.selectedModifiers = [...this.selectedModifiers, mit];
        }
    }

    async openAddMenuModifierSelection() {
        const t = await this.addMenuModifierSelection(null);
        if (t != null) {
            const options: MenuItemModifierSelectionOption[] = [];
            t.options.forEach(o => options.push(new MenuItemModifierSelectionOption({
                menuModifier: o.menuModifier,
                additionalPrice: null
            })));

            const mit = new MenuItemModifierSelection({
                menuModifierSelection: t as MenuModifierSelection,
                sortOrder: this.selectedModifiers.length,
                options
            });
            this.selectedModifiers = [...this.selectedModifiers, mit];
        }
    }

    async openAddMenuModifierGroup() {
        const t = await this.addMenuModifierGroup(null);
        if (t != null) {
            const mit = new MenuItemModifierGroup({
                menuModifierGroup: t as MenuModifierGroup,
                sortOrder: this.selectedModifiers.length
            });
            this.selectedModifiers = [...this.selectedModifiers, mit];
        }
    }

    async openAddMenuTopping() {
        const t = await this.addMenuTopping(null);
        if (t != null) {
            const mit = new MenuItemTopping();
            mit.included = true;
            mit.topping = t;
            mit.sortOrder = this.selectedToppings.length;
            this.selectedToppings = [...this.selectedToppings, mit];
        }
    }

    async openAddMenuSelection() {
        const t = await this.addMenuSelection(null);
        if (t != null) {
            const mit = new MenuItemSelection();
            mit.menuSelection = t;
            mit.sortOrder = this.selectedSelections.length;
            this.selectedSelections = [...this.selectedSelections, mit];
        }
    }

    selected(event) {
        this.availability = event.value;
    }

    onStepChange(event: any): void {
        if (event.selectedIndex === 1) {
            const overlay: any = document.getElementById('beerDescriptionOverlay');
            const textArea: any = document.getElementById('beerDescriptionTextArea');
            if (!!textArea) {
                const label: any = textArea.previousSibling;
                const textAreaStyles = window.getComputedStyle(textArea);
                overlay.style.top = (label.offsetHeight
                    + parseInt(textAreaStyles.marginTop.substring(0, textAreaStyles.marginTop.length - 2), 10)) + 'px';
                overlay.style.width = textArea.offsetWidth + 'px';
                overlay.style.height = textArea.offsetHeight + 'px';
            }
        }
    }

    async openUploadImageModal() {
        if (this.platform.is('desktop')) {
            const modal = await this.modalController.create({
                component: UploadImageModalComponent,
                cssClass: 'menu-modal'
            });

            modal.onDidDismiss().then(async (dataReturned) => {
                if (dataReturned != null && dataReturned.data != null) {
                    await this.uploadImage(dataReturned.data);
                }
            });

            await modal.present();
        } else {
            const image = await this.fileUploadService.getPhoto();
            this.loadingService.present();
            const imgBlob = this.fileUploadService.b64toBlob(image.base64String, `image/${image.format}`);
            this.uploadImage(imgBlob);
        }
    }

    uploadImage(data: any) {
        this.loadingService.present();
        this.fileUploadService.uploadImage(this.menu.orgId, FileUploadType.MENU_ITEM, data).subscribe(async (imageAddress: any) => {
            this.informationFormGroup.controls.imageAddress.setValue(imageAddress.response);
            this.loadingService.dismiss();
        }, async (err) => {
            console.log(err);
            this.loadingService.dismiss();
            await this.toastService.error('Error uploading image.');
        });
    }

    readFile(menuItem: MenuItem, file: any) {
        const reader = new FileReader();
        reader.onload = async () => {
            const imgBlob = new Blob([reader.result], {
                type: file.type
            });

            this.uploadImage(imgBlob);
        };
        reader.readAsArrayBuffer(file);
    }

    async onSubmit() {
        this.loadingService.present();
        const item: MenuItem = new MenuItem(this.informationFormGroup.value);
        if (!item.taxOverride) {
            item.taxOverride = null;
        }
        item.labels = this.selectedLabels;
        item.prices = this.prices;
        item.toppings = this.selectedToppings;
        item.selections = this.selectedSelections;
        item.taxLabels = this.selectedTaxLabels;
        item.availability = this.availability;
        item.modifiers = this.selectedModifiers.filter(m => !!m.menuModifier);
        item.modifierSelections = this.selectedModifiers.filter(m => !!m.menuModifierSelection);
        item.modifierGroups = this.selectedModifiers.filter(m => !!m.menuModifierGroup);

        if (!item.pickupLocationId) {
            item.pickupLocationId = null;
        }

        if (!item.wifiPrinter) {
            item.wifiPrinter = null;
        }

        if (item.id != null) {
            this.menuItemService.update(item).subscribe(
                (menuItem: MenuItem) => {
                    this.loadingService.dismiss();
                    this.close(menuItem);

                }, (error) => this.errorHandler(error)
            );
        } else {
            this.menuItemService.create(item).subscribe(
                (menuItem: MenuItem) => {
                    this.loadingService.dismiss();
                    this.close(menuItem);
                }, (error) => this.errorHandler(error)
            );
        }
    }

    removeImage(event) {
        event.stopPropagation();
        this.informationFormGroup.controls.imageAddress.setValue(null);
    }

    errorHandler(error) {
        this.loadingService.dismiss();
        if (error.status === 422) {
            this.toastService.error('The item is out of sync from a previous change. Please refresh to make changes.');
            this.close();
        } else if (error.status === 424) {
            this.toastService.error('Failed dependency.');
        } else if (error.status === 409) {
            this.toastService.error('The item cannot contain any selections that also include this item.');
        } else {
            this.toastService.error('A server error occurred.');
        }
    }

    protected readonly MenuItemToppingRule = MenuItemToppingRule;
}
